import { useState } from 'react';
import {
  Shield, Users, ScrollText, Search, UserPlus, MoreVertical,
  CheckCircle, XCircle, Loader2, Clock, ChevronDown,
} from 'lucide-react';
import {
  useIamUsers, useIamStats, useIamRoles, useAuditLog,
  useInviteUser, useUpdateUserRole, useDisableUser, useEnableUser,
  type IamUser, type IamRole, type AuditEntry,
} from './api';

/* ─── Role badge colors ─── */
const ROLE_COLORS: Record<string, { bg: string; text: string }> = {
  admin:   { bg: 'bg-red-100',    text: 'text-red-700' },
  ops:     { bg: 'bg-orange-100', text: 'text-orange-700' },
  builder: { bg: 'bg-blue-100',   text: 'text-blue-700' },
  steward: { bg: 'bg-green-100',  text: 'text-green-700' },
  viewer:  { bg: 'bg-gray-100',   text: 'text-gray-600' },
};

const TABS = [
  { id: 'users', label: 'User Directory', icon: Users },
  { id: 'roles', label: 'Roles Overview', icon: Shield },
  { id: 'audit', label: 'Audit Log', icon: ScrollText },
] as const;

type TabId = typeof TABS[number]['id'];

export default function UsersAndAccessPage() {
  const [activeTab, setActiveTab] = useState<TabId>('users');

  return (
    <div className="flex flex-col h-full">
      <div className="border-b bg-white px-6">
        <div className="flex items-center justify-between py-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Users & Access</h1>
            <p className="text-sm text-gray-500 mt-1">Manage platform users, roles, and audit trail</p>
          </div>
        </div>
        <nav className="flex gap-1 -mb-px">
          {TABS.map((t) => (
            <button
              key={t.id}
              onClick={() => setActiveTab(t.id)}
              className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === t.id
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <t.icon className="w-4 h-4" />
              {t.label}
            </button>
          ))}
        </nav>
      </div>

      <div className="flex-1 overflow-auto bg-gray-50 p-6">
        {activeTab === 'users' && <UserDirectoryTab />}
        {activeTab === 'roles' && <RolesOverviewTab />}
        {activeTab === 'audit' && <AuditLogTab />}
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════
   Tab 1: User Directory
   ═══════════════════════════════════════════ */

function UserDirectoryTab() {
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(1);
  const [showInvite, setShowInvite] = useState(false);

  const { data, isLoading } = useIamUsers({
    page,
    limit: 20,
    search: search || undefined,
    role: roleFilter || undefined,
    status: statusFilter || undefined,
  });
  const { data: stats } = useIamStats();

  return (
    <div className="space-y-4">
      {/* Stats mini-cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <StatCard
            label="Total Users"
            value={data?.total ?? stats.total}
            color="blue"
            note={data?.total != null && data.total !== stats.total ? `stats endpoint ${stats.total} — directory page is ${data.total}` : undefined}
          />
          <StatCard label="Active" value={stats.active} color="green" />
          <StatCard label="Disabled" value={stats.disabled} color="red" />
          <StatCard label="Roles Assigned" value={Object.values(stats.role_distribution).reduce((a, b) => a + b, 0)} color="purple" />
        </div>
      )}

      {/* Search + Filters + Invite */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[200px] max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search by name or email..."
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            className="w-full pl-10 pr-4 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <select
          value={roleFilter}
          onChange={(e) => { setRoleFilter(e.target.value); setPage(1); }}
          className="px-3 py-2 border rounded-lg text-sm bg-white"
        >
          <option value="">All Roles</option>
          <option value="admin">Admin</option>
          <option value="ops">Ops</option>
          <option value="builder">Builder</option>
          <option value="steward">Steward</option>
          <option value="viewer">Viewer</option>
        </select>
        <select
          value={statusFilter}
          onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
          className="px-3 py-2 border rounded-lg text-sm bg-white"
        >
          <option value="">All Status</option>
          <option value="active">Active</option>
          <option value="disabled">Disabled</option>
        </select>
        <button
          onClick={() => setShowInvite(!showInvite)}
          className="ml-auto px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium flex items-center gap-2"
        >
          <UserPlus className="w-4 h-4" />
          Invite User
        </button>
      </div>

      {/* Invite form */}
      {showInvite && <InviteForm onClose={() => setShowInvite(false)} />}

      {/* Table */}
      <div className="bg-white rounded-xl border overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 border-b">
            <tr>
              <th className="text-left px-4 py-3 font-medium text-gray-500">User</th>
              <th className="text-left px-4 py-3 font-medium text-gray-500">Role</th>
              <th className="text-left px-4 py-3 font-medium text-gray-500">Status</th>
              <th className="text-left px-4 py-3 font-medium text-gray-500">Last Login</th>
              <th className="text-right px-4 py-3 font-medium text-gray-500">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {isLoading ? (
              [...Array(5)].map((_, i) => (
                <tr key={i}>
                  <td colSpan={5} className="px-4 py-4">
                    <div className="h-4 bg-gray-200 rounded animate-pulse w-3/4" />
                  </td>
                </tr>
              ))
            ) : data?.users?.length ? (
              data.users.map((user) => (
                <UserRow key={user.user_id} user={user} />
              ))
            ) : (
              <tr>
                <td colSpan={5} className="px-4 py-12 text-center text-gray-400">
                  No users found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {data && data.pages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-gray-500">
            Page {page} of {data.pages} ({data.total} users)
          </p>
          <div className="flex gap-2">
            <button disabled={page <= 1} onClick={() => setPage(page - 1)} className="px-3 py-1 border rounded text-sm disabled:opacity-50">Previous</button>
            <button disabled={page >= data.pages} onClick={() => setPage(page + 1)} className="px-3 py-1 border rounded text-sm disabled:opacity-50">Next</button>
          </div>
        </div>
      )}
    </div>
  );
}

function StatCard({ label, value, color, note }: { label: string; value: number; color: string; note?: string }) {
  return (
    <div className="bg-white rounded-xl border p-4">
      <p className="text-sm text-gray-500">{label}</p>
      <p className={`text-2xl font-bold text-${color}-600 mt-1`}>{value}</p>
      {note && <p className="text-[11px] text-amber-800 mt-1">{note}</p>}
    </div>
  );
}

function UserRow({ user }: { user: IamUser }) {
  const [showActions, setShowActions] = useState(false);
  const [editingRole, setEditingRole] = useState(false);
  const updateRole = useUpdateUserRole();
  const disableUser = useDisableUser();
  const enableUser = useEnableUser();

  const primaryRole = user.roles[0] || 'viewer';
  const rc = ROLE_COLORS[primaryRole] || ROLE_COLORS.viewer;

  return (
    <tr className="hover:bg-gray-50 transition-colors">
      <td className="px-4 py-3">
        <div className="flex items-center gap-3">
          {user.photoURL ? (
            <img src={user.photoURL} className="w-8 h-8 rounded-full" alt="" />
          ) : (
            <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center text-gray-500 text-xs font-bold">
              {(user.displayName || user.email || '?')[0]?.toUpperCase()}
            </div>
          )}
          <div>
            <p className="font-medium text-gray-900">{user.displayName || 'Unnamed'}</p>
            <p className="text-xs text-gray-500">{user.email || user.user_id}</p>
          </div>
        </div>
      </td>
      <td className="px-4 py-3">
        {editingRole ? (
          <select
            defaultValue={primaryRole}
            onChange={(e) => {
              updateRole.mutate({ id: user.user_id, role: e.target.value });
              setEditingRole(false);
            }}
            onBlur={() => setEditingRole(false)}
            autoFocus
            className="px-2 py-1 border rounded text-xs"
          >
            <option value="admin">Admin</option>
            <option value="ops">Ops</option>
            <option value="builder">Builder</option>
            <option value="steward">Steward</option>
            <option value="viewer">Viewer</option>
          </select>
        ) : (
          <button
            onClick={() => setEditingRole(true)}
            className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${rc.bg} ${rc.text} hover:opacity-80`}
            title="Click to change role"
          >
            {primaryRole}
            <ChevronDown className="w-3 h-3" />
          </button>
        )}
      </td>
      <td className="px-4 py-3">
        <span className={`inline-flex items-center gap-1.5 text-xs font-medium ${
          user.status === 'active' ? 'text-green-600' : 'text-red-500'
        }`}>
          {user.status === 'active' ? <CheckCircle className="w-3.5 h-3.5" /> : <XCircle className="w-3.5 h-3.5" />}
          {user.status}
        </span>
      </td>
      <td className="px-4 py-3 text-xs text-gray-500">
        {user.last_login_at ? new Date(user.last_login_at).toLocaleDateString() : 'Never'}
      </td>
      <td className="px-4 py-3 text-right relative">
        <button onClick={() => setShowActions(!showActions)} className="p-1 hover:bg-gray-100 rounded">
          <MoreVertical className="w-4 h-4 text-gray-400" />
        </button>
        {showActions && (
          <div className="absolute right-4 top-full z-10 bg-white border rounded-lg shadow-lg py-1 min-w-[140px]">
            <button
              onClick={() => { setEditingRole(true); setShowActions(false); }}
              className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50"
            >
              Change Role
            </button>
            {user.status === 'active' ? (
              <button
                onClick={() => { disableUser.mutate(user.user_id); setShowActions(false); }}
                className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 text-red-600"
              >
                Disable User
              </button>
            ) : (
              <button
                onClick={() => { enableUser.mutate(user.user_id); setShowActions(false); }}
                className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 text-green-600"
              >
                Enable User
              </button>
            )}
          </div>
        )}
      </td>
    </tr>
  );
}

function InviteForm({ onClose }: { onClose: () => void }) {
  const invite = useInviteUser();
  const [form, setForm] = useState({ email: '', displayName: '', role: 'viewer' });
  const [result, setResult] = useState<any>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.email) return;
    try {
      const res = await invite.mutateAsync(form);
      setResult(res);
    } catch (err: any) {
      setResult({ error: err?.message || 'Failed to invite user' });
    }
  };

  return (
    <div className="bg-white rounded-xl border p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold">Invite New User</h3>
        <button onClick={onClose} className="text-gray-400 hover:text-gray-600">&times;</button>
      </div>

      {result?.success ? (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <p className="text-green-800 font-medium">User invited successfully!</p>
          <p className="text-green-700 text-sm mt-1">Email: {result.email}</p>
          <p className="text-green-700 text-sm">Temporary password: <code className="bg-green-100 px-2 py-0.5 rounded">{result.temp_password}</code></p>
          <p className="text-green-600 text-xs mt-2">Share this password securely with the user. They should change it after first login.</p>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
              <input
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder="user@example.com"
                className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Display Name</label>
              <input
                type="text"
                value={form.displayName}
                onChange={(e) => setForm({ ...form, displayName: e.target.value })}
                placeholder="John Smith"
                className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
              <select
                value={form.role}
                onChange={(e) => setForm({ ...form, role: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg text-sm"
              >
                <option value="admin">Admin</option>
                <option value="ops">Ops</option>
                <option value="builder">Builder</option>
                <option value="steward">Steward</option>
                <option value="viewer">Viewer</option>
              </select>
            </div>
          </div>
          {result?.error && (
            <div className="bg-red-50 text-red-700 text-sm p-3 rounded-lg">{result.error}</div>
          )}
          <div className="flex gap-2">
            <button
              type="submit"
              disabled={!form.email || invite.isPending}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium disabled:opacity-50 flex items-center gap-2"
            >
              {invite.isPending ? <><Loader2 className="w-4 h-4 animate-spin" /> Inviting...</> : 'Send Invite'}
            </button>
            <button type="button" onClick={onClose} className="px-4 py-2 border rounded-lg text-sm">Cancel</button>
          </div>
        </form>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════
   Tab 2: Roles Overview
   ═══════════════════════════════════════════ */

function RolesOverviewTab() {
  const { data: roles, isLoading: rolesLoading } = useIamRoles();
  const { data: stats } = useIamStats();

  if (rolesLoading) {
    return <div className="animate-pulse space-y-4">{[...Array(5)].map((_, i) => <div key={i} className="h-24 bg-white rounded-xl border" />)}</div>;
  }

  return (
    <div className="space-y-4">
      <h2 className="text-lg font-semibold text-gray-900">Platform Roles</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {(roles || []).map((role) => {
          const rc = ROLE_COLORS[role.id] || ROLE_COLORS.viewer;
          const count = stats?.role_distribution?.[role.id] || 0;
          return (
            <div key={role.id} className="bg-white rounded-xl border p-6">
              <div className="flex items-center gap-3 mb-3">
                <span className={`px-3 py-1 rounded-full text-sm font-semibold ${rc.bg} ${rc.text}`}>
                  {role.name}
                </span>
                <span className="text-sm text-gray-400">{count} user{count !== 1 ? 's' : ''}</span>
              </div>
              <p className="text-sm text-gray-600">{role.description}</p>
            </div>
          );
        })}
      </div>

      {/* Permissions Matrix */}
      <div className="bg-white rounded-xl border p-6 mt-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Permissions Matrix</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="text-left px-4 py-2 font-medium text-gray-500">Capability</th>
                <th className="text-center px-3 py-2 font-medium text-red-600">Admin</th>
                <th className="text-center px-3 py-2 font-medium text-orange-600">Ops</th>
                <th className="text-center px-3 py-2 font-medium text-blue-600">Builder</th>
                <th className="text-center px-3 py-2 font-medium text-green-600">Steward</th>
                <th className="text-center px-3 py-2 font-medium text-gray-600">Viewer</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {[
                { cap: 'User Management', perms: [true, true, false, false, false] },
                { cap: 'System Configuration', perms: [true, true, false, false, false] },
                { cap: 'Template & Agent Design', perms: [true, true, true, false, false] },
                { cap: 'Data Editing', perms: [true, true, true, true, false] },
                { cap: 'View Dashboard & Reports', perms: [true, true, true, true, true] },
                { cap: 'Secrets Management', perms: [true, true, false, false, false] },
                { cap: 'App Instance Management', perms: [true, true, true, false, false] },
                { cap: 'Entity Management', perms: [true, true, true, true, false] },
              ].map((row) => (
                <tr key={row.cap} className="hover:bg-gray-50">
                  <td className="px-4 py-2 text-gray-700">{row.cap}</td>
                  {row.perms.map((p, i) => (
                    <td key={i} className="text-center px-3 py-2">
                      {p ? <CheckCircle className="w-4 h-4 text-green-500 inline" /> : <XCircle className="w-4 h-4 text-gray-300 inline" />}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════
   Tab 3: Audit Log
   ═══════════════════════════════════════════ */

function AuditLogTab() {
  const [actionFilter, setActionFilter] = useState('');
  const [actorFilter, setActorFilter] = useState('');
  const [page, setPage] = useState(1);

  const { data, isLoading } = useAuditLog({
    page,
    limit: 30,
    action: actionFilter || undefined,
    actor: actorFilter || undefined,
  });

  const ACTION_LABELS: Record<string, { label: string; color: string }> = {
    user_invited: { label: 'User Invited', color: 'bg-blue-100 text-blue-700' },
    role_changed: { label: 'Role Changed', color: 'bg-amber-100 text-amber-700' },
    user_disabled: { label: 'User Disabled', color: 'bg-red-100 text-red-700' },
    user_enabled: { label: 'User Enabled', color: 'bg-green-100 text-green-700' },
    entity_created: { label: 'Entity Created', color: 'bg-purple-100 text-purple-700' },
    entity_updated: { label: 'Entity Updated', color: 'bg-indigo-100 text-indigo-700' },
    entity_deleted: { label: 'Entity Deleted', color: 'bg-red-100 text-red-700' },
  };

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[200px] max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Filter by actor or target..."
            value={actorFilter}
            onChange={(e) => { setActorFilter(e.target.value); setPage(1); }}
            className="w-full pl-10 pr-4 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <select
          value={actionFilter}
          onChange={(e) => { setActionFilter(e.target.value); setPage(1); }}
          className="px-3 py-2 border rounded-lg text-sm bg-white"
        >
          <option value="">All Actions</option>
          <option value="user_invited">User Invited</option>
          <option value="role_changed">Role Changed</option>
          <option value="user_disabled">User Disabled</option>
          <option value="user_enabled">User Enabled</option>
          <option value="entity_created">Entity Created</option>
          <option value="entity_updated">Entity Updated</option>
          <option value="entity_deleted">Entity Deleted</option>
        </select>
      </div>

      {/* Audit entries */}
      <div className="bg-white rounded-xl border overflow-hidden">
        {isLoading ? (
          <div className="p-6 space-y-3">
            {[...Array(5)].map((_, i) => <div key={i} className="h-12 bg-gray-100 rounded animate-pulse" />)}
          </div>
        ) : data?.entries?.length ? (
          <div className="divide-y">
            {data.entries.map((entry) => {
              const al = ACTION_LABELS[entry.action] || { label: entry.action, color: 'bg-gray-100 text-gray-700' };
              return (
                <div key={entry.id} className="px-4 py-3 flex items-start gap-4 hover:bg-gray-50">
                  <div className="flex-shrink-0 pt-0.5">
                    <Clock className="w-4 h-4 text-gray-400" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${al.color}`}>
                        {al.label}
                      </span>
                      <span className="text-sm text-gray-700">
                        by <span className="font-medium">{entry.actor_id}</span>
                        {entry.target_id && (
                          <> on <span className="font-medium">{entry.target_id}</span></>
                        )}
                      </span>
                    </div>
                    {entry.details && Object.keys(entry.details).length > 0 && (
                      <p className="text-xs text-gray-500 mt-1">
                        {Object.entries(entry.details).map(([k, v]) => `${k}: ${JSON.stringify(v)}`).join(' | ')}
                      </p>
                    )}
                  </div>
                  <div className="text-xs text-gray-400 flex-shrink-0">
                    {new Date(entry.timestamp).toLocaleString()}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="p-12 text-center text-gray-400">No audit entries found.</div>
        )}
      </div>

      {/* Pagination */}
      {data && data.pages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-gray-500">Page {page} of {data.pages} ({data.total} entries)</p>
          <div className="flex gap-2">
            <button disabled={page <= 1} onClick={() => setPage(page - 1)} className="px-3 py-1 border rounded text-sm disabled:opacity-50">Previous</button>
            <button disabled={page >= data.pages} onClick={() => setPage(page + 1)} className="px-3 py-1 border rounded text-sm disabled:opacity-50">Next</button>
          </div>
        </div>
      )}
    </div>
  );
}
